import { useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { Receipt, Storefront } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useStars } from '@/contexts/StarContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import { EmbeddedCompanion, useCompanionMoment } from '@/components/character';
import {
  StarsGrid,
  StreakCard,
  StarGoalCard,
  useChildTokens,
  usePendingPurchaseCount,
  useShopHasItems,
} from '@/components/child';
import CreateActivityFab from '@/components/layout/CreateActivityFab';
import VoiceActivityCreator from '@/components/voice/VoiceActivityCreator';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * StarsPage — barnvyns "Mina stjärnor" (prototypens STARS). Barn-only: i
 * vuxen-/handledarläge redirectas till '/'. Visar total räknare, stjärnrutnät,
 * streak och aktiva stjärnmål. Data via StarContext (kontraktet mot stjärn-
 * backenden — nollvärden tills backend svarar).
 *
 * Uppfyll/inlösen är en föräldra-handling som sker i handledarläge (utanför denna
 * barn-only vy), därför ingen fulfill-knapp här. Barnet gör ETT val: vilket av
 * förälderns mål som nästa stjärna ska räknas mot.
 */

const StarsPageInner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tokens = useChildTokens();
  const { balance, streak, goals, selectGoal } = useStars();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { pendingCount, hasAnyPurchase } = usePendingPurchaseCount();
  const { hasItems: shopHasItems } = useShopHasItems();

  useCompanionMoment('happy', 'child.companion.stars');

  const activeGoals = goals.filter((g) => g.status === 'active');
  // Finns mål men inget valt går stjärnorna ingenstans. Då är uppmaningen viktigare än
  // listan och ska stå FÖRE korten, inte gömmas under dem.
  const noGoalSelected = activeGoals.length > 0 && !activeGoals.some((g) => g.selected);

  return (
    <Container sx={{ pb: 12 }}>
      <Stack spacing={2} pt={1.5}>
        <EmbeddedCompanion variant="row" accent="amber" />

        <Typography
          sx={{ fontSize: '3rem', fontWeight: 900, color: tk.primary, textAlign: 'center' }}
        >
          ⭐ {t('child.stars.totalCount', { count: balance.totalEarnedStars })}
        </Typography>

        {/* Ingång till butiken — bara när det FINNS något att köpa. En knapp som leder
            till tomma hyllor är ett löfte som inte infrias. Hooken startar på "inget att
            visa" och svarar först när den vet, så knappen dyker upp i efterhand istället
            för att blinka förbi och försvinna under barnets finger. */}
        {shopHasItems && (
          <Button
            variant="contained"
            startIcon={<Storefront size={22} weight="fill" />}
            onClick={() => navigate('/shop')}
            sx={{
              alignSelf: 'center',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '999px',
              px: 3,
              bgcolor: tk.primary,
            }}
          >
            {t('child.stars.shopCta')}
          </Button>
        )}

        {/* Ingång till "Mina köp" — synlig så fort barnet HAR köpt något, oavsett status.
            Villkoret var tidigare pendingCount > 0, och då försvann knappen barnet nyss
            använde i samma sekund som föräldern löste in sista köpet. Objektspermanens
            väger tyngre än att hålla vyn tom, så det villkoret är borta. hasAnyPurchase
            återinför inte problemet: "har köpt något någonsin" går aldrig bakåt — ett köp
            byter status men försvinner aldrig ur listan. Knappen kan alltså dyka upp, men
            aldrig tas ifrån barnet. Räknaren styrs fortfarande enbart av pendingCount:
            synligheten kommer från totalen, siffran från det som väntar på en vuxen. */}
        {hasAnyPurchase && (
          <Button
            variant="outlined"
            startIcon={<Receipt size={20} />}
            onClick={() => navigate('/shop/purchases')}
            aria-label={
              pendingCount > 0
                ? (t('child.shop.purchases.entryAria', { count: pendingCount }) as string)
                : (t('child.shop.purchases.entryButton') as string)
            }
            sx={{
              alignSelf: 'center',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '999px',
              px: 3,
            }}
          >
            {pendingCount > 0
              ? t('child.shop.purchases.entryButtonCount', { count: pendingCount })
              : t('child.shop.purchases.entryButton')}
          </Button>
        )}

        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: tokens.text, mb: 1 }}>
            {t('child.stars.collectionTitle')}
          </Typography>
          <StarsGrid earned={balance.totalEarnedStars} />
        </Box>

        <StreakCard currentStreak={streak.currentStreak} />

        {activeGoals.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: tokens.text, mb: 1 }}>
              {t('child.stars.goalsTitle')}
            </Typography>
            {noGoalSelected && (
              <Typography
                sx={{ fontSize: '0.9rem', fontWeight: 600, color: tokens.textSoft, mb: 1 }}
              >
                {t('child.stars.goalPickPrompt')}
              </Typography>
            )}
            <Stack spacing={1.25}>
              {activeGoals.map((goal) => (
                <StarGoalCard
                  key={goal.id}
                  goal={goal}
                  canSelect
                  onSelect={(goalId) => void selectGoal(goalId)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>

      {/* Gemensam FAB-position — röstskapande PÅ (konsekvent för alla entrypoints) */}
      <CreateActivityFab onVoiceClick={() => setVoiceOpen(true)} />
      <VoiceActivityCreator
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSaved={() => setVoiceOpen(false)}
      />
    </Container>
  );
};

const StarsPage = () => {
  const effectiveChildMode = useEffectiveChildMode();
  // Barn-only: vuxen-/handledarläge har ingen stjärnvy → tillbaka till start.
  if (!effectiveChildMode) {
    return <Navigate to="/" replace />;
  }
  return <StarsPageInner />;
};

export default StarsPage;
